// Shared document dialogs + helpers — reused by the flat Documents table and the
// folder-tree DocumentBrowser. Extracted verbatim from DocumentsPage (testids kept).
import { useState, useEffect, useMemo } from "react";
import {
    X, Save, FileText, Upload, Download, Loader2,
    CornerUpRight, ShieldCheck, ShieldAlert, Trash2, Eye, CheckCircle2, XCircle, Sparkles, FolderPlus,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";

export const STATUS_TONE = {
    Draft: "neutral", Uploaded: "blue", "Under review": "amber", Approved: "green",
    Rejected: "red", Superseded: "neutral", Expired: "red", Missing: "red",
    Required: "amber", Requested: "amber", "Not applicable": "neutral",
};

export const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";

export function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>
            {children}
        </div>
    );
}

export function formatBytes(bytes) {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Short, human date for table cells: "17 Jun 2026" (date-only). Returns "—" when missing.
export function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function Meta({ label, children }) {
    return (
        <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#9A968F]">{label}</div>
            <div className="text-[#1F1F1F]">{children}</div>
        </div>
    );
}

export function UploadDialog({ clients, buildings, meta, prefill, portalClientId, folders = [], onClose, onCreated }) {
    const [form, setForm] = useState({
        client_id: prefill?.client_id || portalClientId || clients[0]?.id || "",
        building_id: "",
        project_id: prefill?.project_id || "",
        folder_id: prefill?.folder_id || "",
        title: "",
        category: "Reports",
        document_type: "",
        description: "",
        tags: "",
        review_date: "",
        expiry_date: "",
        required_flag: false,
        portal_visible: !!prefill?.project_id,   // project-scoped uploads default visible; account-level defaults hidden
        owner_name: "",
    });
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const filteredBuildings = buildings.filter((b) => !form.client_id || b.client_id === form.client_id);
    const targetFolder = folders.find((f) => f.id === form.folder_id);
    const showHiddenFolderWarning = form.portal_visible && targetFolder && !targetFolder.portal_visible;

    async function submit() {
        if (!file) { toast.error("Pick a file to upload"); return; }
        if (!form.client_id) { toast.error("Client is required"); return; }
        if (!form.title.trim()) { toast.error("Title is required"); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            Object.entries(form).forEach(([k, v]) => {
                if (v === "" || v === null || v === undefined) return;
                fd.append(k, typeof v === "boolean" ? String(v) : v);
            });
            await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document uploaded");
            onCreated();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        }
        setSaving(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="document-upload-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">Upload document</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                    {form.project_id && (
                        <div data-testid="document-project-link" className="flex items-center gap-2 text-[11.5px] font-semibold px-3 py-2 rounded-lg" style={{ background: "#FBEFE5", color: "#9F4212" }}>
                            <FileText size={14} /> Linked to project{prefill?.project_label ? `: ${prefill.project_label}` : ""}
                            {prefill?.folder_label ? ` · folder: ${prefill.folder_label}` : ""}
                        </div>
                    )}
                    <Field label="File *">
                        <input data-testid="document-upload-file" type="file"
                            onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm" />
                        {file && <div className="mt-1 text-[12px] text-[#6F6A64]">{file.name} · {formatBytes(file.size)}</div>}
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Title *">
                            <input data-testid="document-upload-title" value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="e.g. Fire risk assessment – Beacon Tower" className={inputCls} />
                        </Field>
                        <Field label="Category *">
                            <select data-testid="document-upload-category" value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                                {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Client *">
                            <select data-testid="document-upload-client" value={form.client_id}
                                onChange={(e) => setForm({ ...form, client_id: e.target.value, building_id: "" })}
                                disabled={!!portalClientId || !!form.project_id} className={inputCls}>
                                <option value="">— Select —</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Building (optional)">
                            <select data-testid="document-upload-building" value={form.building_id}
                                onChange={(e) => setForm({ ...form, building_id: e.target.value })} className={inputCls}>
                                <option value="">— None —</option>
                                {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Document type">
                            <input value={form.document_type}
                                onChange={(e) => setForm({ ...form, document_type: e.target.value })}
                                placeholder="e.g. PDF certificate" className={inputCls} />
                        </Field>
                        <Field label="Owner name">
                            <input value={form.owner_name}
                                onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Review date">
                            <input type="date" value={form.review_date}
                                onChange={(e) => setForm({ ...form, review_date: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Expiry date">
                            <input type="date" value={form.expiry_date}
                                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className={inputCls} />
                        </Field>
                    </div>
                    <Field label="Tags (comma-separated)">
                        <input value={form.tags}
                            onChange={(e) => setForm({ ...form, tags: e.target.value })}
                            placeholder="fra, beacon-tower, 2026" className={inputCls} />
                    </Field>
                    <Field label="Description">
                        <textarea value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            rows={3} className={inputCls + " h-auto py-2"} />
                    </Field>
                    <div className="flex items-center gap-6 pt-1">
                        <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                            <input data-testid="document-upload-portal-visible" type="checkbox"
                                checked={form.portal_visible}
                                onChange={(e) => setForm({ ...form, portal_visible: e.target.checked })} />
                            Client can see this in their portal
                        </label>
                        <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                            <input type="checkbox" checked={form.required_flag}
                                onChange={(e) => setForm({ ...form, required_flag: e.target.checked })} />
                            Mandatory / required
                        </label>
                    </div>
                    {showHiddenFolderWarning && (
                        <div data-testid="document-upload-hidden-folder-warning"
                            className="flex items-start gap-2 text-[12px] font-medium px-3 py-2 rounded-lg" style={{ background: "#FCF6EA", color: "#9C6B10" }}>
                            <ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
                            This folder is hidden from the portal — the client won't see this document until the folder is published.
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="document-upload-save" onClick={submit} disabled={saving}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Upload
                    </button>
                </div>
            </div>
        </div>
    );
}

export function DocumentDetail({ doc, clientMap, buildingMap, canEdit, canApprove, onClose, onChanged, onDeleted, onDownload, onPreview }) {
    const [busy, setBusy] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectNotes, setRejectNotes] = useState("");
    const [versionFile, setVersionFile] = useState(null);
    const [versionNotes, setVersionNotes] = useState("");
    const [reviewDate, setReviewDate] = useState(doc.review_date || "");
    const [expiryDate, setExpiryDate] = useState(doc.expiry_date || "");
    const datesDirty = reviewDate !== (doc.review_date || "") || expiryDate !== (doc.expiry_date || "");

    useEffect(() => {
        setReviewDate(doc.review_date || "");
        setExpiryDate(doc.expiry_date || "");
    }, [doc.id, doc.review_date, doc.expiry_date]);

    async function saveDates() {
        setBusy(true);
        try {
            const { data } = await api.patch(`/documents/${doc.id}`, {
                review_date: reviewDate || "", expiry_date: expiryDate || "",
            });
            toast.success("Dates updated");
            onChanged(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update dates");
        }
        setBusy(false);
    }

    async function call(path, body) {
        setBusy(true);
        try {
            const { data } = await api.post(`/documents/${doc.id}/${path}`, body || {});
            toast.success("Updated");
            onChanged(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
        }
        setBusy(false);
    }

    async function uploadVersion() {
        if (!versionFile) { toast.error("Pick a file"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", versionFile);
            if (versionNotes) fd.append("notes", versionNotes);
            const { data } = await api.post(`/documents/${doc.id}/versions`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Uploaded version ${data.version_number}`);
            setVersionFile(null); setVersionNotes("");
            onChanged(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        }
        setBusy(false);
    }

    async function softDelete() {
        if (!window.confirm("Delete this document? This is a soft delete and can be restored from the audit log.")) return;
        setBusy(true);
        try {
            await api.delete(`/documents/${doc.id}`);
            toast.success("Document deleted");
            onDeleted();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
        setBusy(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="document-detail-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-start justify-between">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <FileText size={16} className="text-[#9A968F]" />
                            <h3 className="text-base font-bold text-[#1F1F1F] truncate">{doc.title}</h3>
                            <Pill tone={STATUS_TONE[doc.status] || "neutral"}>{doc.status}</Pill>
                        </div>
                        <div className="text-[12px] text-[#9A968F] mt-1">{doc.filename} · {formatBytes(doc.size)} · v{doc.version_number}</div>
                    </div>
                    <button data-testid="document-detail-close" onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>

                <div className="p-5 overflow-y-auto space-y-5">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
                        <Meta label="Category">{doc.category}</Meta>
                        <Meta label="Type">{doc.document_type || "—"}</Meta>
                        <Meta label="Client">{clientMap[doc.client_id]?.name || "—"}</Meta>
                        <Meta label="Building">{buildingMap[doc.building_id]?.name || "—"}</Meta>
                        <Meta label="Owner">{doc.owner_name || "—"}</Meta>
                        <Meta label="Portal visible">{doc.portal_visible ? "Yes" : "No"}</Meta>
                        {canEdit ? (
                            <>
                                <Field label="Review date">
                                    <input data-testid="document-detail-review-date" type="date" value={reviewDate}
                                        onChange={(e) => setReviewDate(e.target.value)} className={inputCls + " h-8"} />
                                </Field>
                                <Field label="Expiry date">
                                    <input data-testid="document-detail-expiry-date" type="date" value={expiryDate}
                                        onChange={(e) => setExpiryDate(e.target.value)} className={inputCls + " h-8"} />
                                </Field>
                            </>
                        ) : (
                            <>
                                <Meta label="Review date">{doc.review_date || "—"}</Meta>
                                <Meta label="Expiry date">{doc.expiry_date || "—"}</Meta>
                            </>
                        )}
                    </div>
                    {canEdit && datesDirty && (
                        <div className="flex items-center justify-end">
                            <button data-testid="document-detail-save-dates" onClick={saveDates} disabled={busy}
                                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
                                style={{ background: "#2E6B57" }}>
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save dates
                            </button>
                        </div>
                    )}

                    {doc.description && (
                        <div className="text-[13px] text-[#1F1F1F] bg-[#F5F3F0] border border-[#E5E0DA] rounded-lg p-3">{doc.description}</div>
                    )}

                    {doc.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {doc.tags.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0EDE8] text-[#6F6A64]">{t}</span>)}
                        </div>
                    )}

                    {doc.versions?.length > 0 && (
                        <div>
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Version history</h4>
                            <ul className="space-y-1.5 text-[12px]">
                                {doc.versions.slice().reverse().map((v) => (
                                    <li key={v.version_number} className="flex justify-between bg-[#F5F3F0] rounded px-3 py-1.5">
                                        <span className="font-mono">v{v.version_number} · {v.filename}</span>
                                        <span className="text-[#9A968F]">{v.uploaded_at?.slice(0, 10)} · {formatBytes(v.size)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {doc.approvals?.length > 0 && (
                        <div>
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Approval activity</h4>
                            <ul className="space-y-1.5 text-[12px]">
                                {doc.approvals.slice().reverse().map((a) => (
                                    <li key={a.id} className="bg-[#F5F3F0] rounded px-3 py-2">
                                        <div className="flex justify-between">
                                            <span className="font-semibold text-[#1F1F1F]">{a.action.replace("_", " ")}</span>
                                            <span className="text-[#9A968F]">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
                                        </div>
                                        <div className="text-[#6F6A64]">{a.actor_name}{a.notes ? ` — ${a.notes}` : ""}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {canEdit && (
                        <div className="border-t border-[#E5E0DA] pt-4">
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Upload new version</h4>
                            <p className="text-[11.5px] text-[#9A968F] mb-2">Uploading a new version keeps the document's review/expiry dates — update them above and click "Save dates" if this version has a new expiry.</p>
                            <div className="flex items-center gap-2">
                                <input data-testid="document-version-file" type="file" className="text-sm flex-1"
                                    onChange={(e) => setVersionFile(e.target.files?.[0] || null)} />
                                <input placeholder="Optional notes" value={versionNotes}
                                    onChange={(e) => setVersionNotes(e.target.value)}
                                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm w-56" />
                                <button data-testid="document-version-upload" onClick={uploadVersion} disabled={busy || !versionFile}
                                    className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50"
                                    style={{ background: "#2A5DB0" }}>
                                    <Upload size={13} /> Upload v{(doc.version_number || 1) + 1}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-between gap-2 bg-[#F5F3F0] flex-wrap">
                    <div className="flex items-center gap-2">
                        <button onClick={() => onPreview && onPreview(doc)} data-testid="document-detail-preview"
                            className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg border border-[#E5E0DA] bg-white text-[#1F1F1F]">
                            <Eye size={13} /> Preview
                        </button>
                        <button onClick={() => onDownload(doc)} data-testid="document-detail-download"
                            className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg border border-[#E5E0DA] bg-white text-[#1F1F1F]">
                            <Download size={13} /> Download
                        </button>
                        {canEdit && ["Draft", "Uploaded", "Rejected"].includes(doc.status) && (
                            <button onClick={() => call("submit-review")} disabled={busy} data-testid="document-submit-review"
                                className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg text-white" style={{ background: "#C68A1C" }}>
                                <CornerUpRight size={13} /> Submit for review
                            </button>
                        )}
                        {canApprove && doc.status === "Under review" && (
                            <>
                                <button onClick={() => call("approve")} disabled={busy} data-testid="document-approve"
                                    className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg text-white" style={{ background: "#3F7D3A" }}>
                                    <ShieldCheck size={13} /> Approve
                                </button>
                                <button onClick={() => setRejectOpen(true)} disabled={busy} data-testid="document-reject"
                                    className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg border border-red-500 text-red-600">
                                    <ShieldAlert size={13} /> Reject
                                </button>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {canEdit && (
                            <button onClick={softDelete} disabled={busy} data-testid="document-delete"
                                className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg text-red-600 hover:bg-red-50">
                                <Trash2 size={13} /> Delete
                            </button>
                        )}
                        <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Close</button>
                    </div>
                </div>
            </div>

            {rejectOpen && (
                <div className="fixed inset-0 z-[60] bg-black/40 grid place-items-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
                        <h4 className="text-base font-bold text-[#1F1F1F] mb-3">Reject document</h4>
                        <p className="text-[13px] text-[#6F6A64] mb-2">Provide a reason — this will be recorded on the document audit.</p>
                        <textarea value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} data-testid="document-reject-notes"
                            rows={3} placeholder="Reason…" className="w-full p-3 rounded-lg border border-[#E5E0DA] bg-white text-sm" />
                        <div className="flex justify-end gap-2 mt-3">
                            <button onClick={() => setRejectOpen(false)} className="px-3 py-2 text-sm text-[#6F6A64]">Cancel</button>
                            <button data-testid="document-reject-confirm" disabled={!rejectNotes.trim() || busy}
                                onClick={async () => { await call("reject", { notes: rejectNotes }); setRejectOpen(false); setRejectNotes(""); }}
                                className="px-3 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-50" style={{ background: "#B43A3A" }}>Confirm reject</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


// ---------------- Bulk upload — AI-assisted folder matching ----------------
// When opened from a specific folder (prefill.folder_id set), every file goes straight
// into that folder like before. When opened at project root, each file gets analysed by
// Claude Sonnet against the project's existing folder tree (filename + short content
// peek) — confident matches are pre-selected, everything else requires the user to pick
// an existing folder or name a brand-new one before "Upload all" is enabled.
const BULK_LIMIT = 15;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const stripExt = (name) => (name || "document").replace(/\.[^.]+$/, "");

export function BulkUploadDialog({ clients, buildings, meta, prefill, portalClientId, folders = [], onClose, onUploaded }) {
    const [clientId, setClientId] = useState(prefill?.client_id || portalClientId || clients[0]?.id || "");
    const [buildingId, setBuildingId] = useState("");
    const [portalVisible, setPortalVisible] = useState(!!prefill?.project_id);
    const [rows, setRows] = useState([]); // {key, file, title, category, status, error, folderChoice, folderId, newFolderName, aiConfident, aiReason, aiAutoNewName}
    const [busy, setBusy] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analyzed, setAnalyzed] = useState(false);
    const categories = meta?.categories || [];
    const defaultCategory = categories.includes("Reports") ? "Reports" : (categories[0] || "Reports");
    const filteredBuildings = buildings.filter((b) => !clientId || b.client_id === clientId);
    const projectId = prefill?.project_id || null;
    const fixedFolderId = prefill?.folder_id || null;
    // AI folder-matching only makes sense against a project's own folder tree — the
    // flat account-level "all documents" bulk uploader (no project context) keeps the
    // simple no-AI behaviour it always had.
    const aiMode = !fixedFolderId && !!projectId;

    const folderOptions = useMemo(() => {
        const byid = Object.fromEntries(folders.map((f) => [f.id, f]));
        const pathOf = (fid) => {
            const parts = []; let cur = byid[fid]; let guard = 0;
            while (cur && guard < 6) { parts.unshift(cur.name); cur = cur.parent_id ? byid[cur.parent_id] : null; guard++; }
            return parts.join(" / ");
        };
        return folders.map((f) => ({ id: f.id, label: pathOf(f.id) })).sort((a, b) => a.label.localeCompare(b.label));
    }, [folders]);

    function addFiles(fileList) {
        const incoming = Array.from(fileList || []);
        setRows((prev) => {
            const space = BULK_LIMIT - prev.length;
            if (incoming.length > space) toast.error(`Up to ${BULK_LIMIT} files at a time — taking the first ${space}.`);
            const take = incoming.slice(0, Math.max(0, space)).map((f, i) => ({
                key: `${Date.now()}-${i}-${f.name}`,
                file: f, title: stripExt(f.name), category: defaultCategory,
                status: f.size > MAX_FILE_BYTES ? "error" : "idle",
                error: f.size > MAX_FILE_BYTES ? "File too large (max 25 MB)" : "",
                folderChoice: "unset", folderId: "", newFolderName: "",
                aiConfident: false, aiReason: "", aiAutoNewName: "",
            }));
            return [...prev, ...take];
        });
        setAnalyzed(false);
    }
    const patchRow = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    const removeRow = (key) => { setRows((prev) => prev.filter((r) => r.key !== key)); setAnalyzed(false); };

    async function analyzeWithAI() {
        const pending = rows.filter((r) => r.status === "idle" || r.status === "error");
        if (!pending.length) { toast.error("Add files first"); return; }
        if (!clientId) { toast.error("Client is required"); return; }
        setAnalyzing(true);
        try {
            const fd = new FormData();
            pending.forEach((r) => fd.append("files", r.file));
            fd.append("client_id", clientId);
            if (projectId) fd.append("project_id", projectId);
            const { data } = await api.post("/documents/ai-match-folders", fd, { headers: { "Content-Type": "multipart/form-data" } });
            (data || []).forEach((m, i) => {
                const r = pending[i];
                if (!r) return;
                if (m.confident && m.folder_id) {
                    patchRow(r.key, { folderChoice: "existing", folderId: m.folder_id, aiConfident: true, aiReason: m.reason || "", aiAutoNewName: "" });
                } else {
                    patchRow(r.key, { aiConfident: false, aiReason: m.reason || "", aiAutoNewName: m.new_folder_name || "" });
                }
            });
            setAnalyzed(true);
            const matched = (data || []).filter((m) => m.confident).length;
            toast.success(`AI matched ${matched} of ${(data || []).length} file(s) to an existing folder`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "AI matching failed");
        }
        setAnalyzing(false);
    }

    function chooseFolder(key, value) {
        if (value === "__new__") patchRow(key, { folderChoice: "new", folderId: "" });
        else if (value === "") patchRow(key, { folderChoice: "unset", folderId: "", newFolderName: "" });
        else patchRow(key, { folderChoice: "existing", folderId: value, newFolderName: "" });
    }

    async function uploadAll() {
        if (!clientId) { toast.error("Client is required"); return; }
        const pending = rows.filter((r) => r.status === "idle" || r.status === "error");
        const valid = pending.filter((r) => r.file.size <= MAX_FILE_BYTES && r.title.trim());
        if (!valid.length) { toast.error("Add files with a title to upload"); return; }
        if (aiMode) {
            const missing = valid.filter((r) => !r.folderId && !(r.folderChoice === "new" && r.newFolderName.trim()));
            if (missing.length) { toast.error(`Choose a folder for ${missing.length} file(s) before uploading`); return; }
        }
        setBusy(true);
        const resolvedFolderIds = {};
        if (aiMode) {
            const nameMap = {};
            for (const r of valid.filter((x) => x.folderChoice === "new")) {
                const nkey = r.newFolderName.trim().toLowerCase();
                if (nameMap[nkey]) { resolvedFolderIds[r.key] = nameMap[nkey]; continue; }
                try {
                    const { data } = await api.post("/folders", { client_id: clientId, project_id: projectId, scope: "project", name: r.newFolderName.trim(), parent_id: null });
                    nameMap[nkey] = data.id;
                    resolvedFolderIds[r.key] = data.id;
                } catch (e) {
                    patchRow(r.key, { status: "error", error: "Could not create folder" });
                }
            }
            valid.filter((x) => x.folderChoice === "existing").forEach((r) => { resolvedFolderIds[r.key] = r.folderId; });
        }
        let ok = 0;
        await Promise.all(valid.map(async (r) => {
            if (aiMode && !resolvedFolderIds[r.key]) return; // folder creation failed above, already flagged
            patchRow(r.key, { status: "uploading", error: "" });
            try {
                const fd = new FormData();
                fd.append("file", r.file);
                fd.append("client_id", clientId);
                fd.append("title", r.title.trim());
                fd.append("category", r.category);
                if (buildingId) fd.append("building_id", buildingId);
                if (projectId) fd.append("project_id", projectId);
                const fid = aiMode ? resolvedFolderIds[r.key] : fixedFolderId;
                if (fid) fd.append("folder_id", fid);
                if (portalVisible) fd.append("portal_visible", "true");
                await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
                patchRow(r.key, { status: "done" });
                ok += 1;
            } catch (e) {
                patchRow(r.key, { status: "error", error: formatApiError(e.response?.data?.detail) || "Upload failed" });
            }
        }));
        setBusy(false);
        if (ok) toast.success(`Uploaded ${ok} file(s)`);
        if (ok < valid.length) toast.error(`${valid.length - ok} file(s) failed`);
        onUploaded && onUploaded();
    }

    const doneCount = rows.filter((r) => r.status === "done").length;
    const allDone = rows.length > 0 && rows.every((r) => r.status === "done");
    const pendingRows = rows.filter((r) => r.status === "idle" || r.status === "error");
    const readyToUpload = !aiMode || pendingRows.every((r) => r.folderId || (r.folderChoice === "new" && r.newFolderName.trim()));

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="document-bulk-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <div>
                        <h3 className="text-base font-bold text-[#1F1F1F]">Bulk upload documents</h3>
                        <p className="text-[12px] text-[#9A968F] mt-0.5">
                            {aiMode
                                ? `Up to ${BULK_LIMIT} files. Beyond AI will suggest which existing folder each file belongs in — confirm or change before uploading.`
                                : fixedFolderId
                                    ? `Up to ${BULK_LIMIT} files, all filed into "${prefill?.folder_label || "this folder"}".`
                                    : `Up to ${BULK_LIMIT} files. The filename becomes the document title; pick a category per file.`}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto">
                    {projectId && (
                        <div data-testid="document-bulk-project-link" className="flex items-center gap-2 text-[11.5px] font-semibold px-3 py-2 rounded-lg" style={{ background: "#FBEFE5", color: "#9F4212" }}>
                            <FileText size={14} /> Linked to project{prefill?.project_label ? `: ${prefill.project_label}` : ""}
                            {prefill?.folder_label ? ` · folder: ${prefill.folder_label}` : ""}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Client *">
                            <select data-testid="document-bulk-client" value={clientId}
                                onChange={(e) => { setClientId(e.target.value); setBuildingId(""); }}
                                disabled={!!portalClientId || !!projectId} className={inputCls}>
                                <option value="">— Select —</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Building (optional)">
                            <select data-testid="document-bulk-building" value={buildingId}
                                onChange={(e) => setBuildingId(e.target.value)} className={inputCls}>
                                <option value="">— None —</option>
                                {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </Field>
                    </div>
                    <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                        <input data-testid="document-bulk-portal-visible" type="checkbox" checked={portalVisible}
                            onChange={(e) => setPortalVisible(e.target.checked)} />
                        Client can see these in their portal (applies to all)
                    </label>

                    <div className="rounded-lg border-2 border-dashed border-[#E5E0DA] p-4 text-center">
                        <label className="cursor-pointer inline-flex flex-col items-center gap-1">
                            <Upload size={20} className="text-[#9A968F]" />
                            <span className="text-[13px] font-semibold text-[#1F1F1F]">Choose files</span>
                            <span className="text-[11px] text-[#9A968F]">{rows.length}/{BULK_LIMIT} selected · 25 MB each</span>
                            <input data-testid="document-bulk-file" type="file" multiple className="hidden"
                                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                        </label>
                    </div>

                    {aiMode && rows.length > 0 && (
                        <div className="flex items-center gap-3">
                            <button data-testid="document-bulk-analyze" onClick={analyzeWithAI} disabled={analyzing || busy}
                                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-60"
                                style={{ background: "#2E6B57" }}>
                                {analyzing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                                {analyzed ? "Re-match with AI" : "Match folders with AI"}
                            </button>
                            {!readyToUpload && (
                                <span data-testid="document-bulk-needs-folder-hint" className="text-[11.5px] font-medium" style={{ color: "#9C6B10" }}>
                                    Choose a folder for every file before uploading
                                </span>
                            )}
                        </div>
                    )}

                    {rows.length > 0 && (
                        <div className="space-y-2" data-testid="document-bulk-rows">
                            {rows.map((r, i) => (
                                <div key={r.key} data-testid={`document-bulk-row-${i}`}
                                    className="rounded-lg border border-[#E5E0DA] p-2 space-y-1.5">
                                    <div className={`grid gap-2 items-center ${aiMode ? "grid-cols-[1fr_150px_1fr_28px]" : "grid-cols-[1fr_150px_28px]"}`}>
                                        <div className="min-w-0">
                                            <input data-testid={`document-bulk-title-${i}`} value={r.title}
                                                onChange={(e) => patchRow(r.key, { title: e.target.value })}
                                                disabled={r.status === "uploading" || r.status === "done"}
                                                className="w-full h-8 px-2 rounded border border-[#E5E0DA] bg-white text-[13px] font-semibold text-[#1F1F1F] disabled:bg-[#F6F4F0]" />
                                            <div className="text-[11px] text-[#9A968F] truncate mt-0.5 flex items-center gap-1.5">
                                                {r.file.name} · {formatBytes(r.file.size)}
                                                {r.status === "uploading" && <Loader2 size={11} className="animate-spin" />}
                                                {r.status === "done" && <span className="text-[#3F7D3A] inline-flex items-center gap-0.5"><CheckCircle2 size={11} /> Uploaded</span>}
                                                {r.status === "error" && <span className="text-red-600 inline-flex items-center gap-0.5"><XCircle size={11} /> {r.error}</span>}
                                            </div>
                                        </div>
                                        <select data-testid={`document-bulk-category-${i}`} value={r.category}
                                            onChange={(e) => patchRow(r.key, { category: e.target.value })}
                                            disabled={r.status === "uploading" || r.status === "done"}
                                            className="h-8 px-2 rounded border border-[#E5E0DA] bg-white text-[12px] disabled:bg-[#F6F4F0]">
                                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        {aiMode && (
                                            <select data-testid={`document-bulk-folder-select-${i}`}
                                                value={r.folderId ? r.folderId : (r.folderChoice === "new" ? "__new__" : "")}
                                                onChange={(e) => chooseFolder(r.key, e.target.value)}
                                                disabled={r.status === "uploading" || r.status === "done"}
                                                className="h-8 px-2 rounded border text-[12px] disabled:bg-[#F6F4F0]"
                                                style={{ borderColor: r.folderId || r.folderChoice === "new" ? "#E5E0DA" : "#E7B04F", background: r.folderId || r.folderChoice === "new" ? "white" : "#FCF6EA" }}>
                                                <option value="">— Choose a folder —</option>
                                                {folderOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                                                <option value="__new__">+ Create new folder…</option>
                                            </select>
                                        )}
                                        <button data-testid={`document-bulk-remove-${i}`} onClick={() => removeRow(r.key)}
                                            disabled={r.status === "uploading"}
                                            className="p-1.5 rounded-md hover:bg-red-50 text-red-500 disabled:opacity-40"><Trash2 size={14} /></button>
                                    </div>
                                    {aiMode && r.folderChoice === "new" && (
                                        <div className="flex items-center gap-1.5 pl-1">
                                            <FolderPlus size={12} className="text-[#9A968F]" />
                                            <input data-testid={`document-bulk-new-folder-name-${i}`} value={r.newFolderName}
                                                onChange={(e) => patchRow(r.key, { newFolderName: e.target.value })}
                                                placeholder="New folder name" disabled={r.status === "uploading" || r.status === "done"}
                                                className="h-7 px-2 rounded border border-[#E5E0DA] bg-white text-[12px] flex-1 max-w-xs disabled:bg-[#F6F4F0]" />
                                        </div>
                                    )}
                                    {aiMode && r.folderId && r.aiConfident && (
                                        <div data-testid={`document-bulk-ai-badge-${i}`} className="flex items-center gap-1 pl-1 text-[11px] font-medium" style={{ color: "#2E6B57" }}>
                                            <Sparkles size={11} /> AI matched{r.aiReason ? `: ${r.aiReason}` : ""}
                                        </div>
                                    )}
                                    {aiMode && !r.folderId && r.folderChoice !== "new" && r.aiReason && (
                                        <div data-testid={`document-bulk-ai-hint-${i}`} className="flex items-center gap-1.5 pl-1 text-[11px]" style={{ color: "#9C6B10" }}>
                                            <span>AI: {r.aiReason}{r.aiAutoNewName ? ` — suggested new folder "${r.aiAutoNewName}"` : ""}</span>
                                            {r.aiAutoNewName && (
                                                <button data-testid={`document-bulk-use-ai-suggestion-${i}`}
                                                    onClick={() => patchRow(r.key, { folderChoice: "new", newFolderName: r.aiAutoNewName })}
                                                    className="underline font-semibold">Use this</button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-between gap-2 bg-[#F5F3F0]">
                    <span className="text-[12px] text-[#6F6A64]">{doneCount > 0 && `${doneCount} uploaded`}</span>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">{allDone ? "Close" : "Cancel"}</button>
                        <button data-testid="document-bulk-upload-all" onClick={uploadAll} disabled={busy || allDone || rows.length === 0 || !readyToUpload}
                            className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                            Upload all ({rows.filter((r) => r.status === "idle").length})
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------- In-browser document preview (before download) ----------------
export function DocumentPreviewDialog({ doc, onClose, onDownload }) {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const name = (doc.filename || "").toLowerCase();
    const mime = (doc.mime_type || "").toLowerCase();
    const isPdf = mime === "application/pdf" || name.endsWith(".pdf");
    const isImage = mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
    const canPreview = isPdf || isImage;

    useEffect(() => {
        let objUrl = null;
        let cancelled = false;
        (async () => {
            if (!canPreview) { setLoading(false); return; }
            try {
                const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
                if (cancelled) return;
                objUrl = URL.createObjectURL(new Blob([resp.data], { type: resp.headers["content-type"] || doc.mime_type }));
                setUrl(objUrl);
            } catch (e) {
                if (!cancelled) setError(formatApiError(e.response?.data?.detail) || "Could not load preview");
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
    }, [doc.id]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" data-testid="document-preview-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden flex flex-col" style={{ height: "90vh" }}>
                <div className="px-5 py-3 border-b border-[#E5E0DA] flex items-center justify-between">
                    <div className="min-w-0 flex items-center gap-2">
                        <FileText size={16} className="text-[#9A968F]" />
                        <h3 className="text-[15px] font-bold text-[#1F1F1F] truncate" data-testid="document-preview-title">{doc.title}</h3>
                        <span className="text-[12px] text-[#9A968F] truncate">{doc.filename} · {formatBytes(doc.size)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => onDownload(doc)} data-testid="document-preview-download"
                            className="inline-flex items-center gap-1 text-[13px] font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            <Download size={13} /> Download
                        </button>
                        <button onClick={onClose} data-testid="document-preview-close" className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={18} /></button>
                    </div>
                </div>
                <div className="flex-1 bg-[#525659] min-h-0 grid place-items-center overflow-auto">
                    {loading ? (
                        <div className="text-white/80 text-sm inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading preview…</div>
                    ) : error ? (
                        <div className="text-white/80 text-sm text-center px-6">{error}</div>
                    ) : !canPreview ? (
                        <div className="text-center px-6" data-testid="document-preview-unsupported">
                            <FileText size={40} className="text-white/50 mx-auto mb-3" />
                            <p className="text-white/90 text-sm font-semibold">Preview isn't available for this file type</p>
                            <p className="text-white/60 text-[12px] mt-1">{doc.filename}</p>
                            <button onClick={() => onDownload(doc)}
                                className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold px-3 py-2 rounded-lg text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                <Download size={13} /> Download to open
                            </button>
                        </div>
                    ) : isPdf ? (
                        <iframe title={doc.title} src={url} className="w-full h-full bg-white" data-testid="document-preview-frame" />
                    ) : (
                        <img src={url} alt={doc.title} className="max-w-full max-h-full object-contain" data-testid="document-preview-image" />
                    )}
                </div>
            </div>
        </div>
    );
}
